import { useEffect, useRef, useState } from "react";
import Plot, { PlotParams } from "react-plotly.js";
import { AutoSizer } from "react-virtualized";

/** Ignore AutoSizer's zero-size first paint so Plotly never mounts into a 0×0 box. */
const MIN_DIMENSION_PX = 1;

/**
 * Wait long enough for concurrent UI motion (e.g. assistant frame spring)
 * to finish before Plotly does expensive first paint work on the main thread.
 */
const INITIAL_MOUNT_DELAY_MS = 280;

/** Coalesce rapid size changes (layout thrash / dock resize) into one replot. */
const RESIZE_DEBOUNCE_MS = 120;

export type AutoSizedPlotProps = Omit<PlotParams, "layout"> & {
  layout: NonNullable<PlotParams["layout"]>;
  chartId?: string;
  className?: string;
};

type Size = { width: number; height: number };

const isUsableSize = (width: number, height: number) =>
  width >= MIN_DIMENSION_PX && height >= MIN_DIMENSION_PX;

type SizedPlotProps = AutoSizedPlotProps & {
  width: number;
  height: number;
};

/**
 * Renders Plotly only after:
 * 1. A short delay so open/layout animations can run on free frames
 * 2. AutoSizer reports a non-zero size
 * Subsequent size changes are debounced to avoid replot storms.
 */
const SizedPlot = ({
  chartId,
  className = "chart",
  layout,
  width,
  height,
  ...plotProps
}: SizedPlotProps) => {
  const [canMountPlot, setCanMountPlot] = useState(false);
  const [stableSize, setStableSize] = useState<Size | null>(null);
  const latestSizeRef = useRef<Size | null>(null);
  const hasCommittedSizeRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const enable = () => {
      if (!cancelled) {
        setCanMountPlot(true);
      }
    };

    const scheduleEnable = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(enable, { timeout: 120 });
      } else {
        enable();
      }
    };

    // Minimum delay keeps Plotly off the main thread during assistant-frame motion.
    timeoutId = window.setTimeout(scheduleEnable, INITIAL_MOUNT_DELAY_MS);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  useEffect(() => {
    if (!isUsableSize(width, height)) {
      return;
    }

    const next = { width: Math.round(width), height: Math.round(height) };
    latestSizeRef.current = next;

    // First usable size commits immediately; later changes are debounced.
    if (!hasCommittedSizeRef.current) {
      hasCommittedSizeRef.current = true;
      setStableSize(next);
      return;
    }

    const timerId = window.setTimeout(() => {
      const latest = latestSizeRef.current;
      if (!latest) {
        return;
      }
      setStableSize((prev) => {
        if (prev && prev.width === latest.width && prev.height === latest.height) {
          return prev;
        }
        return latest;
      });
    }, RESIZE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [width, height]);

  if (!canMountPlot || !stableSize) {
    return <div id={chartId} className={className} style={{ width, height }} />;
  }

  return (
    <div id={chartId} className={className}>
      <Plot
        {...plotProps}
        layout={{
          ...layout,
          width: stableSize.width,
          height: stableSize.height,
        }}
      />
    </div>
  );
};

/**
 * AutoSizer + Plotly wrapper that defers first mount and debounces resizes
 * so heavy chart work does not starve concurrent motion animations.
 */
const AutoSizedPlot = (props: AutoSizedPlotProps) => (
  <AutoSizer>
    {({ width, height }) => <SizedPlot {...props} width={width} height={height} />}
  </AutoSizer>
);

export default AutoSizedPlot;
